import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { buildApiUrl } from "../../lib/api";
import { formatApiError } from "../../lib/apiError";
import "./AccountHub.css";

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "verification", label: "Verification" },
    { key: "funding", label: "Bank relationships" },
    { key: "transfers", label: "Transfers" },
];

function maskAccountNumber(value) {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) {
        return "Not available";
    }
    return `••••${digits.slice(-4)}`;
}

function formatDate(value) {
    if (!value) {
        return "—";
    }
    return new Date(value).toLocaleString();
}

function toBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || "");
            const base64 = result.includes(",") ? result.split(",").pop() : result;
            resolve(base64 || "");
        };
        reader.onerror = () => reject(new Error("Failed to read the selected file."));
        reader.readAsDataURL(file);
    });
}

function AccountHub({ user, onResumeOnboarding, onOpenDashboard, onUserUpdated }) {
    const hasBrokerageAccount = Boolean(user?.alpaca_account_id);
    const isLocalDevAccount = user?.auth_provider === "local_dev";
    const isMockAccount = String(user?.alpaca_account_id || "").startsWith("mock-account-");
    const [activeTab, setActiveTab] = useState("overview");
    const [relationships, setRelationships] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [isLoadingFunding, setIsLoadingFunding] = useState(false);
    const [isLoadingVerification, setIsLoadingVerification] = useState(false);
    const [fundingError, setFundingError] = useState("");
    const [isSavingRelationship, setIsSavingRelationship] = useState(false);
    const [isSavingTransfer, setIsSavingTransfer] = useState(false);
    const [isUploadingDocument, setIsUploadingDocument] = useState(false);
    const [isSyncingAccount, setIsSyncingAccount] = useState(false);
    const [verification, setVerification] = useState(null);
    const [relationshipForm, setRelationshipForm] = useState({
        account_owner_name: "",
        bank_account_type: "checking",
        bank_account_number: "",
        bank_routing_number: "",
        nickname: "",
    });
    const [transferForm, setTransferForm] = useState({
        relationship_id: "",
        amount: "",
        direction: "deposit",
    });
    const [documentForm, setDocumentForm] = useState({
        document_type: "identity_verification",
        document_sub_type: "drivers_license",
        file: null,
        file_name: "",
    });
    const canLinkBank = Boolean(verification?.funding_enabled);
    const canSyncLiveAccount = verification?.broker_sync_available !== false;
    const syncBlockedMessage = verification?.broker_sync_message || "";

    const headline = hasBrokerageAccount
        ? "Account and funding"
        : "brokerage application status";

    const statusLabel = !hasBrokerageAccount
        ? "Application in progress"
        : isMockAccount
            ? "Mock account reference"
            : "Application submitted";

    const statusTone = !hasBrokerageAccount
        ? "pending"
        : isMockAccount
            ? "pending"
            : "ready";

    const handleTabChange = (nextTab) => {
        setFundingError("");
        setActiveTab(nextTab);
    };

    const loadVerification = async () => {
        setIsLoadingVerification(true);
        setFundingError("");
        try {
            const response = await axios.get(buildApiUrl("/funding/verification"), {
                withCredentials: true,
            });
            setVerification(response.data?.verification || null);
        } catch (error) {
            setFundingError(
                error?.response?.data?.message
                || "We could not validate the live brokerage account."
            );
        } finally {
            setIsLoadingVerification(false);
        }
    };

    const handleSyncAccount = async () => {
        setIsSyncingAccount(true);
        setFundingError("");
        try {
            const response = await axios.post(
                buildApiUrl("/funding/account/sync"),
                {},
                { withCredentials: true }
            );
            if (response.data?.user && onUserUpdated) {
                onUserUpdated(response.data.user);
            }
            setVerification(response.data?.verification || null);
            setRelationships(response.data?.relationships || []);
            setTransfers(response.data?.transfers || []);
        } catch (error) {
            setFundingError(
                error?.response?.status === 401
                    ? "Live Alpaca sync is not authorized in this environment yet. Add valid Broker API credentials first."
                    : formatApiError(error, "We could not sync this account with Alpaca.")
            );
        } finally {
            setIsSyncingAccount(false);
        }
    };

    const loadFundingData = async () => {
        if (!canLinkBank) {
            setRelationships([]);
            setTransfers([]);
            return;
        }

        setIsLoadingFunding(true);
        setFundingError("");
        try {
            const [relationshipsResponse, transfersResponse] = await Promise.all([
                axios.get(buildApiUrl("/funding/relationships"), { withCredentials: true }),
                axios.get(buildApiUrl("/funding/transfers"), { withCredentials: true }),
            ]);
            const nextRelationships = relationshipsResponse.data?.relationships || [];
            setRelationships(nextRelationships);
            setTransfers(transfersResponse.data?.transfers || []);
            setTransferForm((prev) => ({
                ...prev,
                relationship_id: prev.relationship_id || nextRelationships[0]?.id || "",
            }));
        } catch (error) {
            setFundingError(
                formatApiError(error, "We could not load funding data.")
            );
        } finally {
            setIsLoadingFunding(false);
        }
    };

    useEffect(() => {
        loadVerification();
    }, [user?.alpaca_account_id]);

    useEffect(() => {
        loadFundingData();
    }, [canLinkBank, user?.alpaca_account_id]);

    const relationshipSummary = useMemo(() => {
        return relationships.map((relationship) => {
            const metadata = relationship.metadata_json || {};
            return {
                ...relationship,
                nickname: relationship.nickname || metadata.nickname || "",
                accountOwnerName: metadata.account_owner_name || metadata.accountOwnerName || "—",
                accountType: metadata.bank_account_type || metadata.accountType || "—",
                accountNumber: metadata.bank_account_number || metadata.accountNumber || "",
                routingNumber: metadata.bank_routing_number || metadata.routingNumber || "",
            };
        });
    }, [relationships]);

    const handleRelationshipSubmit = async (event) => {
        event.preventDefault();
        setIsSavingRelationship(true);
        setFundingError("");
        try {
            await axios.post(buildApiUrl("/funding/relationships"), relationshipForm, {
                withCredentials: true,
            });
            setRelationshipForm({
                account_owner_name: "",
                bank_account_type: "checking",
                bank_account_number: "",
                bank_routing_number: "",
                nickname: "",
            });
            await loadFundingData();
            setActiveTab("funding");
        } catch (error) {
            setFundingError(
                formatApiError(error, "We could not create the bank relationship.")
            );
        } finally {
            setIsSavingRelationship(false);
        }
    };

    const handleTransferSubmit = async (event) => {
        event.preventDefault();
        setIsSavingTransfer(true);
        setFundingError("");
        try {
            const endpoint = transferForm.direction === "withdrawal"
                ? "/funding/withdraw"
                : "/funding/deposit";
            await axios.post(
                buildApiUrl(endpoint),
                {
                    relationship_id: transferForm.relationship_id,
                    amount: transferForm.amount,
                },
                { withCredentials: true }
            );
            setTransferForm((prev) => ({ ...prev, amount: "" }));
            await loadFundingData();
            setActiveTab("transfers");
        } catch (error) {
            setFundingError(
                formatApiError(error, "We could not create the transfer.")
            );
        } finally {
            setIsSavingTransfer(false);
        }
    };

    const handleDocumentSubmit = async (event) => {
        event.preventDefault();
        if (!documentForm.file) {
            setFundingError("Choose a document file before uploading.");
            return;
        }

        setIsUploadingDocument(true);
        setFundingError("");
        try {
            const content = await toBase64(documentForm.file);
            await axios.post(
                buildApiUrl("/funding/verification/documents"),
                {
                    document_type: documentForm.document_type,
                    document_sub_type: documentForm.document_sub_type,
                    mime_type: documentForm.file.type || "application/octet-stream",
                    file_name: documentForm.file_name || documentForm.file.name,
                    content,
                },
                { withCredentials: true }
            );
            setDocumentForm({
                document_type: "identity_verification",
                document_sub_type: "drivers_license",
                file: null,
                file_name: "",
            });
            await loadVerification();
            setActiveTab("verification");
        } catch (error) {
            setFundingError(
                formatApiError(error, "We could not upload the verification document.")
            );
        } finally {
            setIsUploadingDocument(false);
        }
    };

    return (
        <section className="account-hub">
            <div className="account-hub-hero">
                <div>
                    <span className="account-hub-kicker">Account</span>
                    <h2>{headline}</h2>
                    <p>
                        Review application status, link funding sources, and validate
                        whether this user is backed by a real Alpaca brokerage account.
                    </p>
                </div>
                <div className="account-hub-status">
                    <span className={`account-hub-pill ${statusTone}`}>{statusLabel}</span>
                    {isLocalDevAccount ? (
                        <span className="account-hub-pill muted">Local dev account</span>
                    ) : null}
                </div>
            </div>

            <div className="account-hub-tabs">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        className={`account-hub-tab ${activeTab === tab.key ? "active" : ""}`}
                        onClick={() => handleTabChange(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {fundingError ? <div className="account-hub-error">{fundingError}</div> : null}

            {activeTab === "overview" ? (
                <div className="account-hub-grid">
                    <article className="account-hub-card">
                        <span className="account-hub-card-label">Profile</span>
                        <h3>{user?.email || "No email on file"}</h3>
                        <p>
                            {hasBrokerageAccount
                                ? `Brokerage account reference: ${user.alpaca_account_id}`
                                : "No brokerage account is attached yet. Continue onboarding to submit the application."}
                        </p>
                    </article>

                    <article className="account-hub-card">
                        <span className="account-hub-card-label">Validation</span>
                        <h3>{verification?.funding_enabled ? "Ready for bank linking" : "Needs account validation"}</h3>
                        <p>
                            {verification?.funding_enabled
                                ? "Alpaca considers this account funding-ready, so ACH linking and transfer requests can move forward."
                                : verification?.message || "Validate the live Alpaca account before linking a bank."}
                        </p>
                        {!canSyncLiveAccount && syncBlockedMessage ? (
                            <div className="account-hub-note account-hub-note-inline">{syncBlockedMessage}</div>
                        ) : null}
                        <div className="account-hub-inline-actions">
                            <button
                                type="button"
                                className="account-hub-button"
                                onClick={handleSyncAccount}
                                disabled={isSyncingAccount || !canSyncLiveAccount}
                            >
                                {isSyncingAccount
                                    ? "Syncing..."
                                    : verification?.is_mock || !verification?.alpaca_account_id
                                        ? "Create or sync live account"
                                        : "Refresh from Alpaca"}
                            </button>
                        </div>
                    </article>
                </div>
            ) : null}

            {activeTab === "verification" ? (
                <div className="account-hub-stack">
                    <article className="account-hub-card">
                        <span className="account-hub-card-label">Live account status</span>
                        <h3>{isLoadingVerification ? "Checking..." : (verification?.account_status || "Unknown")}</h3>
                        <div className="account-hub-detail-grid">
                            <div>
                                <span className="account-hub-detail-label">Brokerage account id</span>
                                <div>{verification?.alpaca_account_id || "Not created yet"}</div>
                            </div>
                            <div>
                                <span className="account-hub-detail-label">Funding enabled</span>
                                <div>{verification?.funding_enabled ? "Yes" : "No"}</div>
                            </div>
                            <div>
                                <span className="account-hub-detail-label">Documents required</span>
                                <div>{verification?.requires_documents ? "Yes" : "No"}</div>
                            </div>
                            <div>
                                <span className="account-hub-detail-label">Mode</span>
                                <div>{verification?.mode || "unknown"}</div>
                            </div>
                        </div>
                        <p className="account-hub-card-copy">
                            {verification?.additional_information || verification?.message || "Use this tab to validate whether Alpaca has accepted the account for funding."}
                        </p>
                        {!canSyncLiveAccount && syncBlockedMessage ? (
                            <div className="account-hub-note account-hub-note-inline">{syncBlockedMessage}</div>
                        ) : null}
                        <div className="account-hub-inline-actions">
                            <button
                                type="button"
                                className="account-hub-button"
                                onClick={handleSyncAccount}
                                disabled={isSyncingAccount || !canSyncLiveAccount}
                            >
                                {isSyncingAccount ? "Syncing..." : "Sync with Alpaca"}
                            </button>
                        </div>
                    </article>

                    <article className="account-hub-card">
                        <span className="account-hub-card-label">Verification documents</span>
                        <h3>Upload documents if needed</h3>
                        <p>
                            If we ask for ID or address proof, upload it here.
                        </p>

                        {!verification?.is_mock && verification?.alpaca_account_id ? (
                            <form className="account-hub-form" onSubmit={handleDocumentSubmit}>
                                <div className="account-hub-form-grid">
                                    <select
                                        value={documentForm.document_type}
                                        onChange={(event) => setDocumentForm((prev) => ({ ...prev, document_type: event.target.value }))}
                                    >
                                        <option value="identity_verification">Identity verification</option>
                                        <option value="tax_verification">Tax verification</option>
                                        <option value="address_verification">Address verification</option>
                                        <option value="selfie_verification">Selfie verification</option>
                                    </select>
                                    <select
                                        value={documentForm.document_sub_type}
                                        onChange={(event) => setDocumentForm((prev) => ({ ...prev, document_sub_type: event.target.value }))}
                                    >
                                        <option value="drivers_license">Driver&apos;s license</option>
                                        <option value="passport">Passport</option>
                                        <option value="state_id">State ID</option>
                                        <option value="utility_bill">Utility bill</option>
                                        <option value="selfie">Selfie</option>
                                    </select>
                                </div>
                                <input
                                    value={documentForm.file_name}
                                    onChange={(event) => setDocumentForm((prev) => ({ ...prev, file_name: event.target.value }))}
                                    placeholder="Optional label or filename"
                                />
                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    onChange={(event) => setDocumentForm((prev) => ({
                                        ...prev,
                                        file: event.target.files?.[0] || null,
                                        file_name: prev.file_name || event.target.files?.[0]?.name || "",
                                    }))}
                                    required
                                />
                                <button type="submit" className="account-hub-button primary" disabled={isUploadingDocument}>
                                    {isUploadingDocument ? "Uploading..." : "Upload document"}
                                </button>
                            </form>
                        ) : (
                            <div className="account-hub-note">
                                Finish account setup first to upload documents.
                            </div>
                        )}

                        <div className="account-hub-list">
                            {(verification?.documents || []).length ? (verification.documents || []).map((document) => (
                                <div key={document.id} className="account-hub-list-item">
                                    <div>
                                        <strong>{document.document_sub_type || document.document_type}</strong>
                                        <div>{document.mime_type} · {formatDate(document.created_at || document.createdAt)}</div>
                                    </div>
                                    <span className="account-hub-list-status">{document.upload_status || "uploaded"}</span>
                                </div>
                            )) : (
                                <div className="account-hub-note">No documents uploaded yet.</div>
                            )}
                        </div>
                    </article>
                </div>
            ) : null}

            {activeTab === "funding" ? (
                <div className="account-hub-stack">
                    <article className="account-hub-card">
                        <span className="account-hub-card-label">Create bank relationship</span>
                        <h3>Link a bank account</h3>
                        <p>
                            We send the ACH relationship payload to Alpaca using the
                            authenticated user’s brokerage account id.
                        </p>

                        {canLinkBank ? (
                            <form className="account-hub-form" onSubmit={handleRelationshipSubmit}>
                                <input
                                    value={relationshipForm.account_owner_name}
                                    onChange={(event) => setRelationshipForm((prev) => ({ ...prev, account_owner_name: event.target.value }))}
                                    placeholder="Account owner name"
                                    required
                                />
                                <div className="account-hub-form-grid">
                                    <input
                                        value={relationshipForm.bank_routing_number}
                                        onChange={(event) => setRelationshipForm((prev) => ({ ...prev, bank_routing_number: event.target.value }))}
                                        placeholder="Routing number"
                                        required
                                    />
                                    <input
                                        value={relationshipForm.bank_account_number}
                                        onChange={(event) => setRelationshipForm((prev) => ({ ...prev, bank_account_number: event.target.value }))}
                                        placeholder="Account number"
                                        required
                                    />
                                </div>
                                <div className="account-hub-form-grid">
                                    <select
                                        value={relationshipForm.bank_account_type}
                                        onChange={(event) => setRelationshipForm((prev) => ({ ...prev, bank_account_type: event.target.value }))}
                                    >
                                        <option value="checking">Checking</option>
                                        <option value="savings">Savings</option>
                                    </select>
                                    <input
                                        value={relationshipForm.nickname}
                                        onChange={(event) => setRelationshipForm((prev) => ({ ...prev, nickname: event.target.value }))}
                                        placeholder="Nickname"
                                    />
                                </div>
                                <button type="submit" className="account-hub-button primary" disabled={isSavingRelationship}>
                                    {isSavingRelationship ? "Linking..." : "Create bank relationship"}
                                </button>
                            </form>
                        ) : (
                            <div className="account-hub-note">
                                Review application and submit to Alpaca first. The current account reference is still local/mock.
                            </div>
                        )}
                    </article>

                    <article className="account-hub-card">
                        <span className="account-hub-card-label">Existing relationships</span>
                        <h3>{isLoadingFunding ? "Loading..." : `${relationshipSummary.length} linked`}</h3>
                        <div className="account-hub-list">
                            {relationshipSummary.length ? relationshipSummary.map((relationship) => (
                                <div key={relationship.id} className="account-hub-list-item">
                                    <div>
                                        <strong>{relationship.nickname || relationship.accountOwnerName}</strong>
                                        <div>{relationship.accountType} · {maskAccountNumber(relationship.accountNumber)}</div>
                                    </div>
                                    <span className="account-hub-list-status">{relationship.status}</span>
                                </div>
                            )) : (
                                <div className="account-hub-note">No bank relationships yet.</div>
                            )}
                        </div>
                    </article>
                </div>
            ) : null}

            {activeTab === "transfers" ? (
                <div className="account-hub-stack">
                    <article className="account-hub-card">
                        <span className="account-hub-card-label">Create transfer</span>
                        <h3>Move cash</h3>
                        <p>Send a deposit or withdrawal request through the ACH relationship already linked to the Alpaca account.</p>

                        {canLinkBank && relationshipSummary.length ? (
                            <form className="account-hub-form" onSubmit={handleTransferSubmit}>
                                <div className="account-hub-form-grid">
                                    <select
                                        value={transferForm.relationship_id}
                                        onChange={(event) => setTransferForm((prev) => ({ ...prev, relationship_id: event.target.value }))}
                                        required
                                    >
                                        <option value="">Choose bank relationship</option>
                                        {relationshipSummary.map((relationship) => (
                                            <option key={relationship.id} value={relationship.id}>
                                                {relationship.nickname || relationship.accountOwnerName}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={transferForm.direction}
                                        onChange={(event) => setTransferForm((prev) => ({ ...prev, direction: event.target.value }))}
                                    >
                                        <option value="deposit">Deposit</option>
                                        <option value="withdrawal">Withdrawal</option>
                                    </select>
                                </div>
                                <input
                                    value={transferForm.amount}
                                    onChange={(event) => setTransferForm((prev) => ({ ...prev, amount: event.target.value }))}
                                    placeholder="Amount"
                                    required
                                />
                                <button type="submit" className="account-hub-button primary" disabled={isSavingTransfer}>
                                    {isSavingTransfer ? "Submitting..." : "Create transfer"}
                                </button>
                            </form>
                        ) : (
                            <div className="account-hub-note">
                                You need a real Alpaca account and at least one bank relationship before transfers can be created.
                            </div>
                        )}
                    </article>

                    <article className="account-hub-card">
                        <span className="account-hub-card-label">Transfer history</span>
                        <h3>{isLoadingFunding ? "Loading..." : `${transfers.length} transfer${transfers.length === 1 ? "" : "s"}`}</h3>
                        <div className="account-hub-list">
                            {transfers.length ? transfers.map((transfer) => (
                                <div key={transfer.id} className="account-hub-list-item">
                                    <div>
                                        <strong>{transfer.direction}</strong>
                                        <div>${transfer.amount} · {formatDate(transfer.created_at || transfer.createdAt)}</div>
                                    </div>
                                    <span className="account-hub-list-status">{transfer.status}</span>
                                </div>
                            )) : (
                                <div className="account-hub-note">No transfers yet.</div>
                            )}
                        </div>
                    </article>
                </div>
            ) : null}

            <div className="account-hub-actions">
                <button type="button" className="account-hub-button primary" onClick={onResumeOnboarding}>
                    {hasBrokerageAccount ? "Review application" : "Continue application"}
                </button>
                <button type="button" className="account-hub-button" onClick={onOpenDashboard}>
                    Trader dashboard
                </button>
            </div>
        </section>
    );
}

export default AccountHub;
